import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, TypeBadge } from "../components/common";
import { FileText, Users, ClipboardList, Megaphone, ShieldCheck, ArrowRight, Building2, BarChart3 } from "lucide-react";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center mb-4 ${accent}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="font-display text-3xl font-black text-slate-900">{value}</p>
      <p className="text-sm text-slate-500 mt-1">{label}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [opps, setOpps] = useState([]);
  const [stats, setStats] = useState({});
  const [responses, setResponses] = useState([]);

  useEffect(() => {
    api.get("/opportunities").then(({ data }) => setOpps(data)).catch(() => {});
    if (user.role === "smme") {
      api.get("/responses/me").then(({ data }) => setResponses(data)).catch(() => {});
    } else {
      api.get("/reports").then(({ data }) => setStats(data)).catch(() => {});
    }
  }, [user.role]);

  return (
    <div>
      <PageHeader
        eyebrow={`Welcome, ${user.name}`}
        title="Dashboard"
        subtitle={user.role === "smme"
          ? "Your supplier activity at a glance."
          : user.role === "buyer"
          ? `Procurement overview for ${user.mine_name || "your operation"}.`
          : "Full portal administration overview."}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        {user.role === "smme" ? (
          <>
            <Stat icon={FileText} label="Open Opportunities" value={opps.length} accent="bg-blue-50 text-blue-700" />
            <Stat icon={ClipboardList} label="My Responses" value={responses.length} accent="bg-[#FEF9E7] text-[#F2B50C]" />
            <Link to="/app/compliance"><Stat icon={ShieldCheck} label="Compliance Documents" value="Manage" accent="bg-emerald-50 text-emerald-700" /></Link>
            <Link to="/app/profile"><Stat icon={Building2} label="Company Profile" value="Edit" accent="bg-slate-100 text-slate-700" /></Link>
          </>
        ) : (
          <>
            <Stat icon={Users} label="Registered SMMEs" value={stats.total_smmes ?? "–"} accent="bg-blue-50 text-blue-700" />
            <Stat icon={FileText} label="Total Opportunities" value={stats.total_opportunities ?? "–"} accent="bg-[#FEF9E7] text-[#F2B50C]" />
            <Stat icon={ClipboardList} label="Total Responses" value={stats.total_responses ?? "–"} accent="bg-emerald-50 text-emerald-700" />
            <Stat icon={BarChart3} label="My Opportunities" value={stats.my_opportunities ?? "–"} accent="bg-slate-100 text-slate-700" />
          </>
        )}
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold text-slate-900">Latest Opportunities</h2>
        <Link to="/app/opportunities" className="text-sm font-semibold text-[#00427A] flex items-center gap-1 hover:underline">
          View all <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="space-y-3">
        {opps.slice(0, 4).map((o) => (
          <Link key={o.id} to="/app/opportunities" className="block bg-white rounded-lg border border-slate-200 p-4 hover:border-[#00427A] transition-colors">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <TypeBadge type={o.type} />
                  <span className="text-xs text-slate-500">{o.mine_name}</span>
                </div>
                <p className="font-semibold text-slate-900">{o.title}</p>
              </div>
              <span className="text-xs text-slate-400 whitespace-nowrap">Closes {o.closing_date || "—"}{o.closing_time ? ` · ${o.closing_time}` : ""}</span>
            </div>
          </Link>
        ))}
        {opps.length === 0 && <p className="text-slate-500 text-sm">No opportunities posted yet.</p>}
      </div>
    </div>
  );
}
